/**
 * Display preferences: units, map defaults and the default rule industry.
 *
 * One small external store (useSyncExternalStore) so React components and
 * non-React code (map tooltips built as HTML strings) read the same values.
 * Always saved in this browser; when signed in, also saved to the account
 * (PATCH /api/auth/preferences) and loaded from it on sign-in, so they
 * follow the user across devices.
 *
 * The map page still reads its legacy localStorage keys on load
 * (heatwatch:industry, heatwatch:basemap, heatwatch:mapFilters), so this
 * store keeps those in step, and announces changes on SETTINGS_EVENT so an
 * open map applies them immediately.
 */
import { useMemo, useSyncExternalStore } from 'react';
import { api } from './api.js';
import { BASEMAPS, BASEMAP_KEY } from './basemaps.js';
import { INDUSTRIES, DEFAULT_INDUSTRY } from './industries.js';
import { makeUnits, resolveUnits } from './units.js';

export const SETTINGS_EVENT = 'heatwatch:settings';
const PREFS_KEY = 'heatwatch:prefs';
const INDUSTRY_KEY = 'heatwatch:industry';
const MAP_FILTER_KEY = 'heatwatch:mapFilters';
const METRIC_KEYS = ['severity', 'wetBulbF', 'highF', 'gustMph', 'precip24In', 'aqiPm25'];

const storage = {
  get(key) {
    try {
      return localStorage.getItem(key);
    } catch {
      return null;
    }
  },
  set(key, value) {
    try {
      if (value == null) localStorage.removeItem(key);
      else localStorage.setItem(key, value);
    } catch {
      /* storage blocked — the preference applies for this page only */
    }
  },
  json(key) {
    try {
      return JSON.parse(localStorage.getItem(key) ?? 'null');
    } catch {
      return null;
    }
  },
};

const validBasemap = (v) => (BASEMAPS.some((b) => b.key === v) ? v : null);
const validMetric = (v) => (METRIC_KEYS.includes(v) ? v : null);
const validIndustry = (v) => (INDUSTRIES.some((i) => i.key === v) ? v : null);

/** Fill in defaults and drop anything unknown. */
function sanitize(p) {
  return {
    units: resolveUnits(p?.units),
    map: {
      basemap: validBasemap(p?.map?.basemap) ?? 'dark',
      metric: validMetric(p?.map?.metric) ?? 'severity',
    },
    industry: validIndustry(p?.industry) ?? DEFAULT_INDUSTRY,
  };
}

function readLocal() {
  return sanitize({
    units: storage.json(PREFS_KEY)?.units,
    map: { basemap: storage.get(BASEMAP_KEY), metric: storage.json(MAP_FILTER_KEY)?.metric },
    industry: storage.get(INDUSTRY_KEY),
  });
}

let state = null;
let signedIn = false;
const listeners = new Set();

export function getPreferences() {
  if (!state) state = readLocal();
  return state;
}

function subscribe(fn) {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

function writeLocal(next, prev) {
  storage.set(PREFS_KEY, JSON.stringify({ units: next.units }));
  storage.set(INDUSTRY_KEY, next.industry);
  storage.set(BASEMAP_KEY, next.map.basemap);
  if (next.map.metric !== prev.map.metric || next.industry !== prev.industry) {
    const filters = storage.json(MAP_FILTER_KEY) ?? {};
    storage.set(MAP_FILTER_KEY, JSON.stringify({ ...filters, metric: next.map.metric, industry: next.industry }));
  }
}

/**
 * Apply a partial patch ({units: {temperature: 'C'}}, {map: {basemap}}, …).
 * Saved locally right away; returns the account save (resolves immediately
 * when signed out) so callers can report a failure.
 */
export function setPreferences(patch, { remote = true } = {}) {
  const prev = getPreferences();
  const next = sanitize({
    units: { ...prev.units, ...patch?.units },
    map: { ...prev.map, ...patch?.map },
    industry: patch?.industry ?? prev.industry,
  });
  state = next;
  writeLocal(next, prev);

  const detail = {};
  if (next.industry !== prev.industry) detail.industry = next.industry;
  if (next.map.metric !== prev.map.metric) detail.metric = next.map.metric;
  if (next.map.basemap !== prev.map.basemap) detail.basemap = next.map.basemap;
  if (Object.keys(detail).length) window.dispatchEvent(new CustomEvent(SETTINGS_EVENT, { detail }));
  listeners.forEach((fn) => fn());

  if (!remote || !signedIn) return Promise.resolve(next);
  return api('/auth/preferences', { method: 'PATCH', body: next }).then(() => next);
}

/**
 * Call when the signed-in user changes. Loads the account's preferences;
 * an account with none yet adopts this browser's (first sign-in on a
 * device the user already configured).
 */
export async function syncPreferences(user) {
  signedIn = Boolean(user);
  if (!user) return;
  try {
    const { preferences } = await api('/auth/preferences');
    if (preferences && Object.keys(preferences).length) setPreferences(preferences, { remote: false });
    else await api('/auth/preferences', { method: 'PATCH', body: getPreferences() });
  } catch {
    /* offline or accounts unavailable — keep this browser's preferences */
  }
}

/** Test hook: forget cached state so the next read comes from storage. */
export function resetPreferencesForTests() {
  state = null;
  signedIn = false;
}

export function usePreferences() {
  return useSyncExternalStore(subscribe, getPreferences, getPreferences);
}

/** Unit formatters for the current preference (see units.js). */
export function useUnits() {
  const { units } = usePreferences();
  return useMemo(() => makeUnits(units), [units]);
}

/** Current formatters, for non-React code such as map tooltip HTML. */
export const currentUnits = () => makeUnits(getPreferences().units);
